import { createSlice } from "@reduxjs/toolkit";
import { PRE_CHECK_MACHINE_STATES, STUDENT_PRE_CHECK_STATUS } from "./constants";
import studentPreCheckFlow from "./studentPreCheckFlow";

// Create initial state store and reducers
export const studentPreChecksSlice = createSlice({
  name: "studentPreChecks",
  initialState: {
    contextStates: {
      checks: {},
      errors: {},
      troubleshootData: {
        [PRE_CHECK_MACHINE_STATES.networkPingChecks]: {},
        [PRE_CHECK_MACHINE_STATES.networkSpeedChecks]: {},
        [PRE_CHECK_MACHINE_STATES.hardwareChecks]: {},
        [PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks]: {},
        [PRE_CHECK_MACHINE_STATES.studentASRChecks]: {}
      },
      checkData: {
        [PRE_CHECK_MACHINE_STATES.networkPingChecks]: {},
        [PRE_CHECK_MACHINE_STATES.networkSpeedChecks]: {},
        [PRE_CHECK_MACHINE_STATES.hardwareChecks]: {},
        [PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks]: {},
        [PRE_CHECK_MACHINE_STATES.studentASRChecks]: {}
      },
      logStack: [],
      logCount: {}
    },
    currentCheck: PRE_CHECK_MACHINE_STATES.init,
    studentPreChecksStarted: false,
    studentPreChecksEnded: false,
    studentPreCheckFatalExit: false,
    settings: {}
  },
  reducers: {
    updateSettings: (state, action) => {
      state.settings = action.payload.settings
    },
    startStudentPreChecks: (state, action) => {
      state.settings = action.payload.settings
      state.studentPreChecksStarted = true;
      state.currentCheck = studentPreCheckFlow(state).getNextPreCheckItem()
    },
    preCheckItemStarted: (state, action) => {
      state.contextStates.checks[action.payload.item] = STUDENT_PRE_CHECK_STATUS.pending
      if(action.payload.async === true){
        // change it to async pending and go to the next check item immediately
        state.contextStates.checks[action.payload.item] = STUDENT_PRE_CHECK_STATUS.async_pending
        state.currentCheck = studentPreCheckFlow(state).getNextPreCheckItem()
      }
    },
    preCheckItemFinished: (state, action) => {
      state.contextStates.checks[action.payload.item] = STUDENT_PRE_CHECK_STATUS.success
      if(state.contextStates.errors[action.payload.item]){
        // clear the error because it finished successfully
        delete state.contextStates.errors[action.payload.item]
      }
    },
    preCheckItemErrored: (state, action) => {
      state.contextStates.checks[action.payload.item] = STUDENT_PRE_CHECK_STATUS.failed
      state.contextStates.errors[action.payload.item] = action.payload.error
    },
    preCheckItemErroredAndMoveOn: (state, action) => {
      state.contextStates.checks[action.payload.item] = STUDENT_PRE_CHECK_STATUS.failed
      state.currentCheck = studentPreCheckFlow(state).getNextPreCheckItem()
    },
    goToNextPreCheckItem: (state) => {
      state.currentCheck = studentPreCheckFlow(state).getNextPreCheckItem()
    },
    preCheckItemClearError: (state, action) => {
      delete state.contextStates.errors[action.payload.item]
    },
    endStudentPreChecks: (state) => {
      state.studentPreChecksEnded = true
    },
    endStudentPreChecksWithError: (state) => {
      state.studentPreChecksEnded = true
      state.studentPreCheckFatalExit = true
    },
    updateTroubleshootData: (state, action) => {
      state.contextStates.troubleshootData[action.payload.type] = {
        ...state.contextStates.troubleshootData[action.payload.type],
        ...action.payload.data
      }
    },
    updateCheckData: (state, action) => {
      state.contextStates.checkData[action.payload.type] = action.payload.data
    },
    preCheckEvent: (state, action) => { 
      state.contextStates.logStack.push({
        timestamp: Date.now(),
        ...action.payload
      })
      const count = state.contextStates.logCount[`${action.payload.item}_${action.payload.event}`]
      state.contextStates.logCount[`${action.payload.item}_${action.payload.event}`] = count === undefined ? 1 : count+1
    } 
  }
});

// Export available action creators
export const {
  updateSettings,
  startStudentPreChecks,
  preCheckItemStarted,
  preCheckItemFinished,
  preCheckItemErrored,
  preCheckItemErroredAndMoveOn,
  goToNextPreCheckItem,
  endStudentPreChecks,
  endStudentPreChecksWithError,
  preCheckItemClearError,
  updateTroubleshootData,
  updateCheckData,
  preCheckEvent
} = studentPreChecksSlice.actions;

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched
export const exampleThunk = () => (dispatch) => {

};

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const selectStudentPreChecks = (state) => state.studentPreChecks

// Export the reducer
export default studentPreChecksSlice.reducer
